import { Component, StrictMode, useEffect, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, SignInButton, useAuth } from "@clerk/react";
import { App } from "./App";
import { api, setTokenGetter } from "./api";
import { Lion, LoadingScreen } from "./Lion";
import "./styles.css";

// With a Clerk key (production), sign-in goes through Clerk: Google, school domain only, and the
// Worker checks every request again. Without one (local development), the API's DEV_AUTH signs
// requests in as a dev teacher.

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

// Errors anywhere in the app get reported, so a blank screen in someone's browser can be diagnosed.
let reported = 0;
function report(err: unknown) {
  if (reported++ > 5) return;
  const e = err instanceof Error ? err : new Error(String(err));
  void api.reportError({ message: e.message, stack: e.stack ?? "", url: location.href }).catch(() => {});
}
window.addEventListener("error", (e) => report(e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => report(e.reason));

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error) {
    report(error);
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="crash" role="alert">
        <Lion pose="think" size={104} label="Roary, thinking" />
        <h1>Something went wrong</h1>
        <p>BuildLab hit a problem and stopped. Your project is saved. Reloading usually fixes it.</p>
        <code>{this.state.error.message}</code>
        <button type="button" className="primary" onClick={() => location.reload()}>
          Reload
        </button>
      </main>
    );
  }
}

function SignIn() {
  return (
    <main className="signin">
      <div className="signin-card">
        <Lion pose="wave" size={208} bob label="Roary the lion, waving hello" />
        <span className="eyebrow gold">Christian Heritage Academy · 8th Grade STEM</span>
        <h1>BuildLab</h1>
        <p>Build with an agent. Sign in with your school Google account.</p>
        <SignInButton mode="modal">
          <button type="button" className="primary">
            Sign in
          </button>
        </SignInButton>
      </div>
    </main>
  );
}

/** Clerk's state decides what shows; while it loads or refreshes there's a loading screen, never nothing. */
function Gate() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!isSignedIn) return;
    setTokenGetter(() => getToken());
    setReady(true);
  }, [isSignedIn, getToken]);
  // Once signed in, keep the app mounted through Clerk's background session refreshes.
  if (ready && isSignedIn !== false) return <App />;
  if (!isLoaded) return <LoadingScreen text="Loading BuildLab…" />;
  if (!isSignedIn) return <SignIn />;
  return <LoadingScreen text="Signing you in…" />;
}

// Clerk's sign-in box in BuildLab's 8-bit CHA style: pixel frame, navy card, gold Google button,
// Press Start 2P for the title and buttons only (the rest stays readable). CSS variables come from
// styles.css; the box renders in this document, so they apply.
const pixelFrame = "var(--frame), 6px 6px 0 0 rgb(2 12 31 / 0.55)";
const clerkAppearance = {
  variables: {
    colorPrimary: "#f0ad42",
    colorPrimaryForeground: "#041e42",
    colorForeground: "#ffffff",
    colorMutedForeground: "#c7d5ea",
    colorBackground: "#0b2f5f",
    colorInput: "#ffffff",
    colorInputForeground: "#041e42",
    colorDanger: "#ff9b85",
    colorModalBackdrop: "rgb(2 12 31 / 0.82)",
    fontFamily: "Lato, system-ui, sans-serif",
    fontFamilyButtons: "var(--pixel)",
    borderRadius: "0",
  },
  elements: {
    cardBox: { borderRadius: 0, border: 0, boxShadow: pixelFrame, margin: "6px" },
    card: { background: "var(--navy-2)", borderRadius: 0, boxShadow: "none" },
    headerTitle: { fontFamily: "var(--pixel)", fontWeight: 400, fontSize: "1.05rem", lineHeight: 1.5, color: "var(--gold)", textShadow: "3px 3px 0 #9e3f2c" },
    headerSubtitle: { fontSize: "1rem", color: "#dbe4ef" },
    socialButtonsBlockButton: {
      background: "var(--gold)",
      color: "var(--navy)",
      borderRadius: 0,
      border: 0,
      boxShadow: "var(--frame), inset 0 -4px 0 0 var(--gold-2), inset 0 4px 0 0 var(--gold-3)",
      margin: "3px",
      padding: "0.9rem 1rem",
      "&:hover": { background: "var(--gold-3)" },
      "&:focus-visible": { outline: "3px solid #fff", outlineOffset: "4px" },
    },
    socialButtonsBlockButtonText: { fontFamily: "var(--pixel)", fontSize: "0.68rem", fontWeight: 400, color: "var(--navy)" },
    footer: { background: "var(--navy)", borderRadius: 0, "& *": { color: "#c7d5ea" } },
    modalCloseButton: { color: "#fff" },
  },
};
const clerkText = {
  signIn: { start: { title: "Sign in to BuildLab", subtitle: "Use your school Google account", titleCombined: "Sign in to BuildLab", subtitleCombined: "Use your school Google account" } },
};

function Root() {
  if (!clerkKey) return <App />;
  return (
    <ClerkProvider publishableKey={clerkKey} appearance={clerkAppearance} localization={clerkText}>
      <Gate />
    </ClerkProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <Root />
    </ErrorBoundary>
  </StrictMode>,
);
